import { useIntl } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export type WithdrawalPasswordFormType = z.infer<ReturnType<typeof useNewWithdrawalPasswordFormSchema>>;
export type UpdateWithdrawalPasswordFormType = z.infer<ReturnType<typeof useUpdateWithdrawalPasswordFormSchema>>;

const useBaseWithdrawalPasswordFormSchema = () => {
	const { $t } = useIntl();

	const NewPasswordSchema = z
		.string()
		.min(
			8,
			$t('requirements.common.form.errors.length', {
				field: $t('settings.withdrawalPassword.password'),
				max: 20,
				min: 8,
				value: $t('requirements.common.form.errors.short')
			})
		)
		.max(
			20,
			$t('requirements.common.form.errors.length', {
				field: $t('settings.withdrawalPassword.password'),
				max: 20,
				min: 8,
				value: $t('requirements.common.form.errors.long')
			})
		)
		.regex(/^.*[0-9].*$/, $t('settings.withdrawalPassword.errors.atLeastOneNumber'))
		.regex(/^.*[a-z].*$/, $t('settings.withdrawalPassword.errors.atLeastOneLowercase'))
		.regex(/^.*[A-Z].*$/, $t('settings.withdrawalPassword.errors.atLeastOneUppercase'));

	return {
		confirmNewPassword: z.string().nonempty(
			$t('requirements.common.form.errors.required', {
				field: $t('settings.withdrawalPassword.confirmPassword')
			})
		),
		newPassword: NewPasswordSchema
	};
};

const useNewWithdrawalPasswordFormSchema = () => z.object(useBaseWithdrawalPasswordFormSchema());

const useUpdateWithdrawalPasswordFormSchema = () => {
	const { $t } = useIntl();
	const { confirmNewPassword: confirmNewPasswordSchema, newPassword: newPasswordSchema } =
		useBaseWithdrawalPasswordFormSchema();

	return z
		.object({
			confirmNewPassword: confirmNewPasswordSchema,
			currentPassword: z.string().nonempty(
				$t('requirements.common.form.errors.required', {
					field: $t('settings.withdrawalPassword.currentPassword')
				})
			),
			newPassword: newPasswordSchema
		})
		.superRefine(({ currentPassword, newPassword }, ctx) => {
			if (currentPassword === newPassword) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: $t('settings.withdrawalPassword.errors.newPasswordMustBeDifferent'),
					path: ['newPassword']
				});
			}
		});
};

export const useWithdrawalPasswordForm = () => {
	const { $t } = useIntl();
	const passwordMustBeDifferentError = $t('settings.withdrawalPassword.errors.newPasswordMustBeDifferent');

	const newWithdrawalPasswordForm = useForm<WithdrawalPasswordFormType>({
		defaultValues: {
			confirmNewPassword: '',
			newPassword: ''
		},
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useNewWithdrawalPasswordFormSchema())
	});

	const updateWithdrawalPasswordForm = useForm<UpdateWithdrawalPasswordFormType>({
		defaultValues: {
			confirmNewPassword: '',
			currentPassword: '',
			newPassword: ''
		},
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useUpdateWithdrawalPasswordFormSchema())
	});

	updateWithdrawalPasswordForm.watch(({ currentPassword, newPassword }) => {
		const currentError = updateWithdrawalPasswordForm.getFieldState('newPassword').error;
		const errorIsPasswordMustBeDifferent = currentError?.message === passwordMustBeDifferentError;

		if (errorIsPasswordMustBeDifferent && newPassword && newPassword !== currentPassword) {
			updateWithdrawalPasswordForm.clearErrors('newPassword');
		} else if (!currentError && newPassword && newPassword === currentPassword) {
			updateWithdrawalPasswordForm.setError('newPassword', {
				message: passwordMustBeDifferentError,
				type: 'custom'
			});
		}
	});

	return {
		newWithdrawalPasswordForm,
		updateWithdrawalPasswordForm
	};
};
